/**
 * Typed client for /users/* — the staff account-management surface
 * (Publishers / Admins / Super Creators). Ported from the publisher
 * dashboard's views/user/list + views/user/info (audit 2026-06-09).
 *
 * These endpoints return RAW shapes (no `{ success, data }` envelope), so
 * we hit `client` directly and read `r.data` — same convention as
 * adminSupport.ts. The list is a mongoose-paginate page. Auth is
 * requireAuth + roleAuthorization([admin, super_publisher]), satisfied by
 * the console's admin magic-link JWT.
 */
import { client } from './client';

export type UserRole = 'publisher' | 'admin' | 'super_publisher' | 'shareholder';
export type AccountStatus = 'active' | 'pending' | 'inactive' | 'rejected';

export interface AdminUser {
  _id: string;
  name?: string;
  email: string;
  companyName?: string;
  phone?: string;
  role?: UserRole;
  status?: AccountStatus;
  publisherKey?: string;
  isPrimary?: boolean;
  payoutCurrency?: string;
  installs?: number;
  uninstalls?: number;
  live?: number;
  liveTime?: number;
  earnings?: number;
  referrals?: number;
  createdAt?: string;
}

/** GET /users/:id adds platform-share rates on top of the user doc. */
export interface UserDetail extends AdminUser {
  /** KYC tier (pending / tier1_lite_verified / tier2_full_verified / escalation_required / blocked). */
  kycTier?: string;
  liveTimeRate?: number;
  liveRate?: number;
  installsRate?: number;
  uninstallsRate?: number;
}

/** mongoose-paginate envelope returned by GET /users. */
export interface PagedUsers {
  docs: AdminUser[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface ListUsersParams {
  role: UserRole;
  search?: string;
  status?: AccountStatus | '';
  page?: number;
  limit?: number;
}

export async function listUsers(params: ListUsersParams): Promise<PagedUsers> {
  const q: Record<string, string | number> = {
    role: params.role,
    page: params.page ?? 1,
    limit: params.limit ?? 20
  };
  if (params.search) q.search = params.search;
  if (params.status) q.status = params.status;
  const r = await client.get<PagedUsers>('/users', { params: q });
  return r.data;
}

export async function fetchUser(id: string): Promise<UserDetail> {
  const r = await client.get<UserDetail>(`/users/${id}`);
  return r.data;
}

export async function approveUser(id: string): Promise<AdminUser> {
  const r = await client.post<AdminUser>(`/users/${id}/approve`);
  return r.data;
}

export async function rejectUser(id: string): Promise<AdminUser> {
  const r = await client.post<AdminUser>(`/users/${id}/reject`);
  return r.data;
}

export async function setPrimaryUser(id: string): Promise<AdminUser> {
  const r = await client.post<AdminUser>(`/users/${id}/setPrimary`);
  return r.data;
}

export interface UpdateUserInput {
  role?: UserRole;
  status?: AccountStatus;
  name?: string;
  companyName?: string;
  payoutCurrency?: string;
}

export async function updateUser(
  id: string,
  patch: UpdateUserInput
): Promise<AdminUser> {
  const r = await client.patch<AdminUser>(`/users/${id}`, patch);
  return r.data;
}

/**
 * Publishers assigned to a super-creator (the legacy "assigns" sub-view).
 * The endpoint shape varies (array or { docs }), so we normalize.
 */
export async function listSuperCreatorPublishers(
  id: string
): Promise<AdminUser[]> {
  const r = await client.get<PagedUsers | AdminUser[]>(
    `/users/get-publishers/${id}`
  );
  const d = r.data;
  if (Array.isArray(d)) return d;
  return d.docs ?? [];
}
